import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { RestaurantService } from './restaurant.service';

@Injectable()
export class NavService {
    data: any = [];

    constructor(
        private router: Router,
        private restaurantService: RestaurantService,
        private location: Location,
    ) {}

    /**
     * Navega para `url` (prefixada pelo slug, ver `parseURL`).
     *
     * ## O contrato, que quase ninguém consome
     *
     * `push` JÁ propaga tudo o que o roteador faz, e é importante que continue
     * assim — mas quase nenhum chamador olha:
     *
     *  - **resolve `true`**  → navegou;
     *  - **resolve `false`** → um guard barrou. `navigateByUrl` NÃO rejeita
     *    nesse caso (é o motivo de o `AppComponent` testar o booleano);
     *  - **rejeita**         → guard/resolver lançou, ou o chunk da rota lazy não
     *    baixou (`ChunkLoadError`). Como `push` é `async` e faz `await`, a
     *    rejeição sai como promessa rejeitada — não há `catch` engolindo nada
     *    aqui, e não pode passar a haver;
     *  - **nunca assenta**   → o roteador não devolve. É o modo de falha medido
     *    em 01/09/2026 no boot (~334 devices/dia numa tela branca, sem erro
     *    nenhum registrado), e o único remédio é PRAZO — `catch` não cobre
     *    promessa que não rejeita.
     *
     * ## Por que o tratamento mora em quem chama, não aqui
     *
     * São 79 sites de `navService.push(...)` no app; 77 são fire-and-forget, sem
     * `await`, sem `then` e sem `catch`. Passar a tratar a falha aqui dentro
     * (engolir, redirecionar, recarregar) aplicaria uma política única a 77
     * telas cujo desfecho correto é diferente em cada uma — e engolir seria pior
     * do que o problema, porque apagaria o `unhandledrejection` que hoje é a
     * única pista que sobra dessas 77.
     *
     * Quem tem política definida trata: o `AppComponent` envolve a navegação de
     * ENTRADA em `comPrazo` e cai no cardápio quando ela não volta
     * (`navegarNoBoot`), porque ali "nenhuma tela" é o pior desfecho possível.
     */
    async push(url: string, data: any = '') {
        this.data = data;
        return await this.router.navigateByUrl(this.parseURL(url));
    }

    pop() {
        this.location.back();
    }

    navigateBackward(url: string = '') {
        if (!url || url === '/') return this.location.back();
        this.router.navigateByUrl(this.parseURL(url));
    }

    async setRoot(url: string, data: any = '') {
        this.data = data;
        return await this.router.navigateByUrl(this.parseURL(url), { replaceUrl: true });
    }

    get(key: string) {
        return this.data.hasOwnProperty(key) ? this.data[key] : false;
    }

    /**
     * Monta a URL de navegação prefixando o slug do restaurante.
     *
     * ## Por que a guarda existe
     *
     * Esta linha lia `restaurant.info.restaurant_url` sem guarda nenhuma, e
     * `parseURL` é chamado por `push`, `setRoot` e `navigateBackward` — ou seja,
     * por TODA navegação do app. Sem restaurante carregado ela lançava
     * `TypeError`, e o efeito não era um erro na tela: era **a navegação não
     * acontecer**. O usuário ficava olhando uma tela parada, sem mensagem e sem
     * saída, que é o sintoma do MP-569.
     *
     * Medido em 15/08/2026, iOS, 7 dias: entre os devices que erram em
     * `restaurant.info`, **62,6% nunca chegam a ver o cardápio** (226 de 361) —
     * contra 1,0% de todos os outros `TypeError` somados. É o erro que mais
     * trava sessão no app, e não é o de maior volume.
     *
     * ## De onde vem o slug quando não há restaurante
     *
     * **Da própria URL.** O slug é o primeiro segmento do caminho — é de lá que
     * o app o tira no boot —, e ele continua ali mesmo quando a carga do
     * restaurante falhou. Preservá-lo é o que faz a tela de erro ser útil: o
     * botão "tentar de novo" do `menu.page` recarrega a página, e sem o slug ele
     * recarregaria numa loja **diferente** da que o cliente quis abrir.
     *
     * Se nem na URL houver slug (o caso `/app/` puro), a rota vai sem prefixo.
     * Toda rota do app vive sob `:restaurant` (ver `app-routing.module.ts`),
     * então ela ainda casa e o roteador monta a página do cardápio — a única que
     * sabe se explicar sem restaurante: `<app-not-found>` quando a loja não
     * existe, ou o convite a tentar de novo quando a carga estourou o prazo.
     *
     * Ou seja: entregar uma tela que se explica, em vez de nenhuma tela.
     *
     * ## Cuidado ao mexer
     *
     * Não basta trocar por `?.` e deixar `slug` virar `undefined`: a URL sairia
     * como `"undefined/menu"`, que casa com `:restaurant` e navega para uma loja
     * inexistente — trocaria a tela parada por uma tela errada.
     */
    parseURL(url = '') {
        if (url.charAt(0) === '/') url = url.substring(1);

        const slug = this.slugDoRestaurante() || this.slugDaURL();
        if (!slug) return url || NavService.ROTA_SEM_RESTAURANTE;

        return slug + (url && url !== '/' ? `/${url}` : '');
    }

    /**
     * Segmento usado quando não há restaurante, não há slug na URL e não há rota
     * pedida. Precisa ser um segmento qualquer não vazio: `navigateByUrl('')`
     * não casa com `:restaurant` e não levaria a tela nenhuma. É o caminho que o
     * `DataGuardService` percorre quando chama `setRoot('')`.
     */
    static readonly ROTA_SEM_RESTAURANTE = 'menu';

    /** Slug em minúsculas, ou `''` quando não há restaurante carregado. */
    private slugDoRestaurante(): string {
        const url = this.restaurantService.restaurant?.info?.restaurant_url;
        return typeof url === 'string' ? url.toLowerCase() : '';
    }

    /**
     * Primeiro segmento do caminho atual — o slug, quando existe.
     *
     * `Location.path()` já vem sem o base href (`/app/`) e pode trazer query
     * string, daí os dois cortes. Nunca lança: isto roda no caminho de toda
     * navegação, e uma exceção aqui reproduziria exatamente o defeito que a
     * guarda veio consertar.
     */
    private slugDaURL(): string {
        try {
            const caminho = String(this.location.path() || '');
            const primeiro = caminho.split('?')[0].split('#')[0]
                .split('/').filter((s) => s.length > 0)[0];
            return primeiro ? primeiro.toLowerCase() : '';
        } catch (e) {
            return '';
        }
    }
}
